<template>
  <div class="auth-page">
    <div class="auth-bg"></div>
    <div class="auth-grid"></div>

    <div class="auth-container">
      <div class="auth-card">
        <!-- Header -->
        <div class="auth-header">
          <div class="auth-logo">
            <div class="auth-logo-icon">CS</div>
            <span class="auth-logo-text">Casino<span class="accent">Script</span></span>
          </div>
          <h1 class="auth-title">{{ $t('auth.reset_password_title') }}</h1>
          <p class="auth-subtitle">{{ $t('auth.reset_password_subtitle') }}</p>
        </div>

        <!-- Success Message -->
        <div v-if="isSuccess" class="auth-success">
          <div class="success-icon-wrap">
            <svg viewBox="0 0 24 24" fill="currentColor" class="success-icon">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          </div>
          <h2 class="success-title">{{ $t('auth.reset_password_success_title') }}</h2>
          <p class="success-text">{{ $t('auth.reset_password_success_text') }}</p>
          <router-link to="/login" class="btn-signin">{{ $t('auth.sign_in') }}</router-link>
        </div>

        <!-- Invalid Token Error -->
        <div v-else-if="tokenError" class="auth-success">
          <div class="success-icon-wrap">
            <svg viewBox="0 0 24 24" fill="currentColor" class="error-icon-lg">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
          </div>
          <h2 class="success-title">{{ $t('auth.reset_password_invalid_title') }}</h2>
          <p class="success-text">{{ $t('auth.reset_password_invalid_text') }}</p>
          <router-link to="/forgot-password" class="btn-signin">{{ $t('auth.reset_password_request_new') }}</router-link>
        </div>

        <!-- Reset Password Form -->
        <template v-else>
          <!-- Error Alert -->
          <div v-if="errorMessage" class="auth-error">
            <span class="auth-error-icon">!</span>
            <span>{{ errorMessage }}</span>
          </div>

          <form class="auth-form" @submit.prevent="handleSubmit">
            <!-- New Password -->
            <div class="form-group">
              <label class="form-label" for="password">{{ $t('auth.new_password_label') }}</label>
              <div class="form-input-wrap">
                <svg class="form-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
                </svg>
                <input
                  id="password"
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  class="form-input"
                  :placeholder="$t('auth.new_password_placeholder')"
                  required
                  minlength="8"
                  autocomplete="new-password"
                />
                <button
                  type="button"
                  class="form-input-toggle"
                  @click="showPassword = !showPassword"
                  tabindex="-1"
                >
                  <svg v-if="!showPassword" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd" />
                  </svg>
                  <svg v-else viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clip-rule="evenodd" />
                    <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                  </svg>
                </button>
              </div>
              <p v-if="fieldErrors.password" class="form-error">{{ fieldErrors.password }}</p>
            </div>

            <!-- Confirm Password -->
            <div class="form-group">
              <label class="form-label" for="password_confirmation">{{ $t('auth.confirm_password_label') }}</label>
              <div class="form-input-wrap">
                <svg class="form-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
                </svg>
                <input
                  id="password_confirmation"
                  v-model="passwordConfirmation"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  class="form-input"
                  :placeholder="$t('auth.confirm_new_password_placeholder')"
                  required
                  minlength="8"
                  autocomplete="new-password"
                />
                <button
                  type="button"
                  class="form-input-toggle"
                  @click="showConfirmPassword = !showConfirmPassword"
                  tabindex="-1"
                >
                  <svg v-if="!showConfirmPassword" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd" />
                  </svg>
                  <svg v-else viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clip-rule="evenodd" />
                    <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                  </svg>
                </button>
              </div>
              <p v-if="fieldErrors.password_confirmation" class="form-error">{{ fieldErrors.password_confirmation }}</p>
            </div>

            <!-- Submit Button -->
            <button type="submit" class="btn-signin" :disabled="isLoading">
              <span v-if="isLoading" class="btn-spinner"></span>
              <span v-else>{{ $t('auth.reset_password_submit') }}</span>
            </button>
          </form>

          <!-- Footer -->
          <div class="auth-footer">
            {{ $t('auth.remember_password') }} <router-link to="/login">{{ $t('auth.sign_in') }}</router-link>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'

const route = useRoute()
const { t } = useI18n()

const password = ref('')
const passwordConfirmation = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const isLoading = ref(false)
const isSuccess = ref(false)
const tokenError = ref(false)
const errorMessage = ref('')
const fieldErrors = ref<Record<string, string>>({})

const token = ref('')
const email = ref('')

onMounted(() => {
  token.value = (route.query.token as string) || ''
  email.value = (route.query.email as string) || ''

  if (!token.value || !email.value) {
    tokenError.value = true
  }
})

async function handleSubmit() {
  errorMessage.value = ''
  fieldErrors.value = {}
  isLoading.value = true

  try {
    await apiClient.post('/api/auth/reset-password', {
      email: email.value,
      token: token.value,
      password: password.value,
      password_confirmation: passwordConfirmation.value,
    })

    isSuccess.value = true
  } catch (e: unknown) {
    const axiosError = e as { response?: { data?: { message?: string; errors?: Record<string, string[]> }; status?: number } }
    const status = axiosError.response?.status

    if (status === 422) {
      const errors = axiosError.response?.data?.errors
      if (errors) {
        for (const [field, messages] of Object.entries(errors)) {
          fieldErrors.value[field] = messages[0]
        }
        if (errors.token || errors.email) {
          tokenError.value = true
        }
      }
      if (!Object.keys(fieldErrors.value).length) {
        errorMessage.value = axiosError.response?.data?.message || t('auth.reset_error_invalid_token')
        tokenError.value = true
      }
    } else if (status === 429) {
      errorMessage.value = t('auth.reset_error_rate_limited')
    } else {
      errorMessage.value = axiosError.response?.data?.message || t('auth.error_generic')
      if (axiosError.response?.data?.message?.toLowerCase().includes('invalid') ||
          axiosError.response?.data?.message?.toLowerCase().includes('expired')) {
        tokenError.value = true
      }
    }
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-primary);
  overflow: hidden;
  position: relative;
}

.auth-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  background-image:
    radial-gradient(ellipse at 20% 50%, rgba(0, 231, 1, 0.05) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 20%, rgba(20, 117, 225, 0.04) 0%, transparent 50%);
  pointer-events: none;
}

.auth-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(rgba(0, 231, 1, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 231, 1, 0.02) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
  z-index: 0;
}

.auth-container {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 420px;
  padding: var(--space-5);
}

.auth-card {
  background: linear-gradient(135deg, rgba(26, 44, 56, 0.95) 0%, rgba(33, 55, 67, 0.85) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-xl);
  padding: var(--space-10);
  backdrop-filter: blur(20px);
  box-shadow: var(--shadow-lg);
  animation: slideUp 0.6s ease both;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header */
.auth-header {
  text-align: center;
  margin-bottom: var(--space-8);
}

.auth-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  margin-bottom: var(--space-5);
}

.auth-logo-icon {
  width: 40px;
  height: 40px;
  background: var(--accent-primary);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000;
  font-size: 14px;
  font-weight: 700;
  font-family: var(--font-family-display);
  box-shadow: var(--shadow-glow-green);
}

.auth-logo-text {
  font-family: var(--font-family-display);
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
}

.auth-logo-text .accent {
  color: var(--accent-primary);
}

.auth-title {
  font-size: var(--font-size-h1);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin-bottom: var(--space-2);
}

.auth-subtitle {
  font-size: var(--font-size-body);
  color: var(--text-tertiary);
}

/* Success / Error States */
.auth-success {
  text-align: center;
}

.success-icon-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: var(--space-4);
}

.success-icon {
  width: 56px;
  height: 56px;
  color: var(--accent-primary);
}

.error-icon-lg {
  width: 56px;
  height: 56px;
  color: var(--color-danger);
}

.success-title {
  font-size: var(--font-size-h2);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin-bottom: var(--space-3);
}

.success-text {
  font-size: var(--font-size-body);
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: var(--space-6);
}

/* Error Alert */
.auth-error {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  background: var(--color-danger-bg);
  border: 1px solid var(--color-danger-border);
  border-radius: var(--radius-md);
  color: var(--color-danger-light);
  font-size: var(--font-size-body);
  margin-bottom: var(--space-4);
}

.auth-error-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: var(--radius-full);
  background: var(--color-danger);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Form */
.auth-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-secondary);
}

.form-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.form-icon {
  position: absolute;
  left: 12px;
  width: 16px;
  height: 16px;
  color: var(--text-tertiary);
  pointer-events: none;
}

.form-input {
  width: 100%;
  background: rgba(15, 25, 35, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-md);
  padding: 12px 16px;
  padding-left: 40px;
  color: var(--text-primary);
  font-size: var(--font-size-body);
  font-family: var(--font-family-primary);
  transition: all var(--transition-fast);
}

.form-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
  background: rgba(15, 25, 35, 1);
}

.form-input::placeholder {
  color: var(--text-tertiary);
}

.form-input-toggle {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--text-tertiary);
  transition: color var(--transition-fast);
  display: flex;
  align-items: center;
}

.form-input-toggle:hover {
  color: var(--text-secondary);
}

.form-input-toggle svg {
  width: 18px;
  height: 18px;
}

.form-error {
  font-size: var(--font-size-sm);
  color: var(--color-danger-light);
}

/* Submit Button */
.btn-signin {
  width: 100%;
  padding: 12px 16px;
  background: var(--accent-primary);
  color: var(--color-on-accent);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-bold);
  font-family: var(--font-family-primary);
  border-radius: var(--radius-md);
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast);
  letter-spacing: 0.02em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-height: 44px;
  text-decoration: none;
}

.btn-signin:hover:not(:disabled) {
  background: var(--accent-primary-hover);
  box-shadow: var(--shadow-glow-green);
  transform: translateY(-2px);
}

.btn-signin:active:not(:disabled) {
  transform: translateY(0);
}

.btn-signin:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-top-color: #000;
  border-radius: var(--radius-full);
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Footer */
.auth-footer {
  text-align: center;
  font-size: 13px;
  color: var(--text-tertiary);
}

.auth-footer a {
  color: var(--accent-secondary);
  text-decoration: none;
  transition: all var(--transition-fast);
}

.auth-footer a:hover {
  color: var(--text-primary);
  text-decoration: underline;
}

/* Responsive */
@media (max-width: 480px) {
  .auth-card {
    padding: 28px;
  }

  .auth-title {
    font-size: 20px;
  }
}
</style>
